import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Assessment, Package, Participant, Project, Report } from '../../models';
import { ImageFallbackDirective } from '../../img-fallback.directive';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { ApiService, DisposableUrl } from '../../api.service';
import { catchError, finalize, of } from 'rxjs';
import { ProjectDomainService } from '../../domain-services/project-domain.service';
import { ParticipantDomainService } from '../../domain-services/participant-domain.service';
import { AssessmentDomainService } from '../../domain-services/assessment-domain.service';
import { ReportDomainService } from '../../domain-services/report-domain.service';
import { PackageDomainService } from '../../domain-services/package-domain.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { UserPermissionsService } from '../../user-permissions.service';

@Component({
  selector: 'app-selected-participant',
  standalone: true,
  templateUrl: './selected-participant.component.html',
  styleUrls: ['./selected-participant.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    ImageFallbackDirective,
    MatButtonModule,
    FormsModule,
    MatSnackBarModule,
  ],
})
export class SelectedParticipantComponent {
  reports = input<Report[]>();
  packages = input<Package[]>();
  route = inject(ActivatedRoute);

  // Reactively track route param changes so the selected
  // participant updates when a different row is clicked.
  private readonly routeParams = toSignal(this.route.paramMap, {
    initialValue: this.route.snapshot.paramMap,
  });

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly participantDomain = inject(ParticipantDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  private readonly snackBar = inject(MatSnackBar);
  readonly permissions = inject(UserPermissionsService);

  project = computed<Project | null>(() => {
    return this.projectDomain.currentProject() ?? null;
  });

  participant = computed<Participant | null>(() => {
    const participants = this.participantDomain.projectParticipants();
    if (!participants) {
      return null;
    }
    const id = this.routeParams().get('participantId');
    if (!id) {
      return null;
    }
    return participants.find((p) => p.id === id) ?? null;
  });

  participantAssessments = computed<Assessment[] | null>(() => {
    const projectAssessments = this.assessmentDomain.projectAssessments();
    const participant = this.participant();
    if (!projectAssessments || !participant) {
      return null;
    }
    return projectAssessments.filter((a) => a.participantId === participant.id);
  });

  reportsForDisplay = computed<Report[]>(() => {
    return this.reportDomain.participantReports() ?? this.reports() ?? [];
  });

  packagesForDisplay = computed<Package[]>(() => {
    return this.packageDomain.participantPackages() ?? this.packages() ?? [];
  });

  isEditing = signal(false);
  participantSaving = signal(false);
  resendingInvite = signal(false);

  editFirstName = signal('');
  editLastName = signal('');
  editEmail = signal('');
  editUniqueId = signal('');

  firstNameInvalid = computed(() => this.editFirstName().trim() === '');
  lastNameInvalid = computed(() => this.editLastName().trim() === '');
  emailInvalid = computed(() => this.editEmail().trim() === '');

  formInvalid = computed(() => {
    return this.firstNameInvalid() || this.lastNameInvalid() || this.emailInvalid();
  });

  constructor() {
    effect(() => {
      const participantId = this.routeParams().get('participantId');
      if (!participantId) {
        return;
      }

      if (!this.reportDomain.participantReports()) {
        this.reportDomain
          .loadParticipantReports(participantId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }

      if (!this.packageDomain.participantPackages()) {
        this.packageDomain
          .loadParticipantPackages(participantId)
          .pipe(catchError(() => of([])))
          .subscribe();
      }
    });
  }

  /* Download Project Reports */
  private apiService = inject(ApiService);
  error = signal<string | null>(null);
  downloading = signal(false);
  private reportStatuses = signal<Record<string, number>>({});

  // Reset edit state and cached statuses when the selected
  // participant changes.
  private readonly participantChangeEffect = effect(() => {
    const p = this.participant();
    if (!p) {
      return;
    }

    this.isEditing.set(false);
    this.participantSaving.set(false);
    this.reportStatuses.set({});
  });
  readonly statusEffect = effect(() => {
    const participant = this.participant();
    const clientId = sessionStorage.getItem('activeClient');
    const currentReports = this.reportsForDisplay();
    const currentPackages = this.packagesForDisplay();

    if (!participant || !clientId) {
      return;
    }

    // Always refresh status for visible reports and packages
    for (const r of currentReports) {
      this.apiService.getParticipantReportStatus(clientId, participant.id, r.id).subscribe({
        next: (status) => this.setReportStatus(r.id, status),
        error: () => {
          // Ignore status errors; UI will fall back to default behavior.
        },
      });
    }

    for (const p of currentPackages) {
      this.apiService.getParticipantPackageStatus(clientId, participant.id, p.id).subscribe({
        next: (status) => this.setReportStatus(p.id, status),
        error: () => {
          // Ignore status errors; UI will fall back to default behavior.
        },
      });
    }
  });

  getReportStatus(reportId: string): number | null {
    const map = this.reportStatuses();
    return reportId in map ? map[reportId] : null;
  }

  private setReportStatus(reportId: string, status: number) {
    const current = this.reportStatuses();
    this.reportStatuses.set({ ...current, [reportId]: status });
  }

  startEdit() {
    const participant = this.participant();
    if (!participant) {
      return;
    }

    this.editFirstName.set(participant.firstName ?? '');
    this.editLastName.set(participant.lastName ?? '');
    this.editEmail.set(participant.email ?? '');
    this.editUniqueId.set(participant.uniqueId ?? '');
    this.isEditing.set(true);
  }

  cancelEdit() {
    this.isEditing.set(false);
  }

  saveEdit() {
    const current = this.participant();
    if (!current) {
      return;
    }

    const firstName = this.editFirstName().trim();
    const lastName = this.editLastName().trim();
    const email = this.editEmail().trim();
    const uniqueId = this.editUniqueId().trim();

    const updated: Participant = {
      ...current,
      // Treat empty strings as null so the backend
      // accepts the payload without 500s.
      firstName: (firstName === '' ? null : firstName) as any,
      lastName: (lastName === '' ? null : lastName) as any,
      email,
      uniqueId: (uniqueId === '' ? null : uniqueId) as any,
    };

    this.error.set(null);
    this.participantSaving.set(true);

    this.participantDomain
      .updateParticipant(updated)
      .pipe(finalize(() => this.participantSaving.set(false)))
      .subscribe({
        next: () => {
          this.isEditing.set(false);
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Failed to update participant.';
          this.error.set(message);
          this.isEditing.set(false);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  resendInvite() {
    const participant = this.participant();
    if (!participant) {
      return;
    }

    this.error.set(null);
    this.resendingInvite.set(true);

    this.participantDomain
      .resendInvite(participant.id)
      .pipe(finalize(() => this.resendingInvite.set(false)))
      .subscribe({
        next: () => {
          this.snackBar.open('Invite sent successfully.', 'Close', { duration: 5000 });
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Failed to resend invite.';
          this.error.set(message);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  download(item: Report, isPackage: boolean) {
    let localP = this.participant();
    if (localP == null) return;

    this.error.set(null);
    this.downloading.set(true);

    const clientId = sessionStorage.getItem('activeClient');
    if (!clientId) {
      this.error.set('Missing client context.');
      return;
    }

    const downloadName = this.buildDownloadFilename(localP, item, isPackage);

    this.apiService
      .downloadReports({
        clientId,
        participantId: localP.id,
        reportId: item.id,
      })
      .pipe(finalize(() => this.downloading.set(false)))
      .subscribe({
        next: ({ url, revoke }) => {
          const a = document.createElement('a');
          a.href = url;
          a.download = downloadName;
          a.click();
          // revoke after the browser processes the click
          requestAnimationFrame(revoke);
        },
        error: (err) => this.error.set('Failed to download.'),
      });
  }

  // Filenames follow the convention:
  //   '{Participant name} - {report/package name} - {report/package norm}.{ext}'
  // where ext is 'pdf' for reports and 'zip' for packages.
  private buildDownloadFilename(
    participant: Participant,
    item: Report,
    isPackage: boolean,
  ): string {
    const participantName = [participant.firstName, participant.lastName]
      .filter((part) => part != null && part.trim() !== '')
      .join(' ')
      .trim();
    const ext = isPackage ? 'zip' : 'pdf';
    const base = [participantName, item.name, item.norm]
      .map((part) => (part ?? '').trim())
      .filter((part) => part !== '')
      .join(' - ');

    return `${this.sanitizeFilename(base)}.${ext}`;
  }

  private sanitizeFilename(value: string): string {
    // Replace characters not permitted in filenames across platforms.
    return value.replace(/[\\/:*?"<>|]/g, '_').trim();
  }

  regenerate(reportId: string) {
    const participant = this.participant();
    const project = this.project();
    const clientId = sessionStorage.getItem('activeClient');

    if (!participant || !project || !clientId) {
      return;
    }

    this.error.set(null);
    this.setReportStatus(reportId, 1);

    this.apiService
      .generateParticipantReport(clientId, project.id, participant.id, reportId)
      .subscribe({
        next: () => {
          // Optionally re-query status; for now we leave it at 1 until refreshed.
        },
        error: () => {
          this.setReportStatus(reportId, 2);
          this.error.set('Failed to regenerate report.');
        },
      });
  }
}
